import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Observable } from 'rxjs';
import type { TableModel } from 'src/models/TableModel';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type {
    ConnectionStatusRefetchEvent,
    ConnectionStatusSignal,
    TableRowCountObservableSignal,
    TableRowCountSignal,
    TableTotalRowCountSignal,
} from 'src/types/EventBus/index';

export type ContainerTableSliceSignals = ConnectionStatusSignal &
    TableRowCountSignal &
    TableRowCountObservableSignal &
    TableTotalRowCountSignal;

export type ContainerTableSliceEvents = ConnectionStatusRefetchEvent;

export type ContainerTableSliceEventBus = EventBus<ContainerTableSliceSignals, ContainerTableSliceEvents>;

interface ContainerTableSliceInit {
    table: TableModel;
}

export interface ContainerTableSliceModel {
    readonly eventBus: ContainerTableSliceEventBus;
}

export const ContainerTableSlice: ModelConstructor<ContainerTableSliceModel, [ContainerTableSliceInit]> = createModel<
    ContainerTableSliceModel,
    [ContainerTableSliceInit]
>((init: ContainerTableSliceInit) => {
    const tableRowCountObservable = signal<Observable<number> | undefined>(init.table.count$);
    const tableTotalRowCount = signal<number | null>(null);

    const connectionStatus: ReadonlySignal<ConnectionStatusSignal['widget::connectionStatus']['value']> = computed(
        () => ({
            status: init.table.connectionStatus.value,
            progress: init.table.progress.value,
            failureCount: init.table.failureCount.value,
        }),
    );

    const eventBus: ContainerTableSliceEventBus = EventBusBuilder.create()
        .addSignal('widget::connectionStatus', () => connectionStatus)
        .addSignal('widget::tableRowCount', () => init.table.countSignal)
        .addSignal('widget::tableRowCountObservable', () => tableRowCountObservable)
        .addSignal('widget::tableTotalRowCount', () => tableTotalRowCount)
        .addEvent('widget::connectionStatusRefetch', () => {
            init.table.refetch();
        })
        .build();

    return {
        eventBus,
    };
});

export type ContainerTableSlice = Model<ContainerTableSliceModel>;
